import { CheckboxInput } from "@nova/component-checkbox-input"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectAllWorkspaceMembers,
    setActiveMember,
} from "features/entity-caching/members/UserManagementSlice"
import { InlineTextInput } from "@nova/component-inline-text-input"
import { useCallback, useMemo, useState } from "react"
import {
    ButtonInline,
    ButtonInlineIcon,
    ButtonPrimary,
    ButtonSecondary,
    PluginButton,
} from "@nova/component-btn"
import {
    ID,
    Licenses,
    MemberType,
    Permissions,
    WorkspaceConfirmationModalName,
} from "@nova/constants"
import { selectUserID } from "user/profile/selectors"
import { showConfirmationModal } from "features/content-overlay/thunks"
import { useTranslation } from "@nova/use-translation"
import { Trans } from "react-i18next"
import * as Icons from "@nova/component-icons"
import { sortMembersInTable } from "features/data-management/workspace/module"
import { TableMember } from "features/data-management/workspace/types"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { Tooltip } from "panels/content/top-bar/TODO-put-in-package/Tooltip"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "features/modals/types"
import { UsersTableHeaders } from "./UsersTableHeaders"
import { WorkspaceCollaboratorProfile } from "../collaborators/WorkspaceCollaboratorProfile"
import { showGlobalModal } from "runlevel/actions"
import { SupportButton } from "panels/routing/SettingsRoutes"
import {
    getGenericWorkspacePage,
    getGenericWorkspacePageHeader,
} from "panels/routing/WorkspaceRoutes"
import { openWorkspaceShareModal } from "features/data-management/workspace/context/thunks"
import { useRouter } from "pages"
import { UserProfileModalTrigger } from "features/users/UserProfileModal"

export const UsersSettingsPage = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({
        title: "Users",
        rightItems: [SupportButton, ManageSubscriptionsButton, ShareButton],
    }),
    Content: UsersTable,
})

function UsersTable() {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const workspaceId = useAppSelector(selectActiveWorkspaceId()) || ""
    const registeredMembers = useAppSelector(selectAllWorkspaceMembers(workspaceId))
    const selfId = useAppSelector(selectUserID)

    //members list has to at least have yourself, so if the list is empty we are still loading
    const isLoading = !registeredMembers || registeredMembers.length === 0

    const [searchFilter, setSearchFilter] = useState("")
    const [filterKeys, setFilterKeys] = useState({
        primaryKey: "licenseOrder",
        secondaryKey: "fullName",
        sortDirection: "asc",
    })
    const [selectedMembers, setSelectedMembers] = useState<string[]>([])

    const sortedAndFilteredMembers = useMemo(() => {
        const filtered = registeredMembers.filter((member) => {
            const searchTerm = `${member.type === MemberType.Member && member.fullName} ${member.type === MemberType.Member ? member.email : member.userId}`
            return searchTerm.toLowerCase().includes(searchFilter.toLowerCase())
        })
        const sortedAndFilteredMembers = sortMembersInTable(
            filtered,
            filterKeys.primaryKey,
            filterKeys.secondaryKey,
            filterKeys.sortDirection as "asc" | "desc"
        )
        return sortedAndFilteredMembers
    }, [registeredMembers, filterKeys, searchFilter])

    //members selected for removing filtered through members that are currently visible in the table
    const [filteredSelectedMembers, filteredSelectedMemberIds] = useMemo(() => {
        const memberIds = sortedAndFilteredMembers.map((member) => member.userId)
        const filteredSelectedMemberIds = selectedMembers.filter((id) => memberIds.includes(id))
        const filteredSelectedMembers = sortedAndFilteredMembers.filter((member) =>
            filteredSelectedMemberIds.includes(member.userId)
        )
        return [filteredSelectedMembers, filteredSelectedMemberIds]
    }, [sortedAndFilteredMembers, selectedMembers])

    const handleMemberSelect = useCallback((memberId: string, checked: boolean) => {
        if (checked) {
            setSelectedMembers((prevValues) => [...prevValues, memberId])
        } else {
            setSelectedMembers((prevValues) => {
                const prevValuesCopy = [...prevValues]
                prevValuesCopy.splice(prevValuesCopy.indexOf(memberId), 1)
                return prevValuesCopy
            })
        }
    }, [])

    const handleAllMemberSelect = useCallback(
        (checked: boolean) => {
            if (checked) {
                const memberIds: string[] = []
                sortedAndFilteredMembers.forEach((member) => {
                    if (
                        member.userId !== selfId &&
                        member.permissions.workspaceAccess !== Permissions.Admin
                    )
                        memberIds.push(member.userId)
                })
                setSelectedMembers(memberIds)
            } else {
                setSelectedMembers([])
            }
        },
        [sortedAndFilteredMembers, selfId]
    )

    const openRemoveCollaboratorsModal = () => {
        dispatch(
            showConfirmationModal({
                type: WorkspaceConfirmationModalName.RemoveCollaborators,
                entityId: workspaceId,
                collaborators: filteredSelectedMembers,
            })
        )
    }

    return (
        <div className="w-full border border-gray-700 rounded-xl">
            <div className="h-[4rem] flex justify-between items-center p-4 w-full">
                <div className="flex items-center gap-2 text-gray-0">
                    <Trans i18nKey={"Filter:"} />
                    <InlineTextInput
                        name="member-filter"
                        placeholder="Search for collaborator"
                        inputClassName="!border-[#404040] text-xs !text-gray-0 !w-[11.25rem] placeholder:!text-gray-0"
                        value={searchFilter}
                        handleChange={(value) => setSearchFilter(value)}
                    />
                    {searchFilter.length > 0 && (
                        <ButtonInlineIcon
                            icon={Icons.XClose}
                            className="scale-75 !m-0 !p-0"
                            onClick={() => setSearchFilter("")}
                        />
                    )}
                </div>
                {filteredSelectedMembers.length > 0 && (
                    <ButtonInline
                        label={`Remove Access (${filteredSelectedMembers.length})`}
                        onClick={openRemoveCollaboratorsModal}
                        className="!bg-gray-700 !h-[2rem] rounded-md text-red-500 font-semibold"
                    />
                )}
            </div>
            <div className="table-container relative scrollable-area rounded-b-[12px]">
                <table className="w-full">
                    <UsersTableHeaders
                        primaryKey={filterKeys.primaryKey}
                        handleAllMembersSelect={handleAllMemberSelect}
                        setFilterKeys={setFilterKeys}
                    />
                    {!isLoading && (
                        <tbody className="text-gray-0">
                            {sortedAndFilteredMembers?.map((member) => (
                                <UsersTableRow
                                    key={member.userId}
                                    member={member}
                                    selectedIds={filteredSelectedMemberIds}
                                    handleMemberSelect={handleMemberSelect}
                                />
                            ))}
                        </tbody>
                    )}
                </table>
                {isLoading && (
                    <div className="flex justify-center items-center p-4 w-full">
                        <NovaSpinner width={24} height={24} />
                    </div>
                )}
                {sortedAndFilteredMembers.length === 0 && !isLoading && (
                    <div className="flex justify-center items-center p-4 w-full">
                        {t("No results found")}
                    </div>
                )}
            </div>
        </div>
    )
}

const UsersTableRow = ({
    member,
    selectedIds,
    handleMemberSelect,
}: {
    member: TableMember
    selectedIds: ID[]
    handleMemberSelect: (memberId: string, checked: boolean) => void
}) => {
    const selfId = useAppSelector(selectUserID)
    const { fullName, userId, email } = member
    const isSelf = userId === selfId
    const { isEditor, isAdmin } = useWorkspacePermissions()

    const isMemberAdmin = member.permissions.workspaceAccess === Permissions.Admin
    const canRemove = isEditor && !isAdmin ? !isMemberAdmin : isAdmin

    const rowClasses = "px-[20px] py-[8px] h-[60px] capitalize"
    const isSelected = selectedIds.includes(userId)

    const isPending = member.type === MemberType.Invite
    return (
        <tr>
            <td className={classNames(rowClasses, "max-w-[15px]")}>
                {!isSelf && canRemove && (
                    <CheckboxInput
                        id="select-all-members"
                        key={`${isSelected}+${userId}`}
                        onChange={(checked) => handleMemberSelect(userId, checked)}
                        value={isSelected}
                        isSmall
                    />
                )}
            </td>
            <td className={rowClasses}>
                <div className="max-w-[13rem]">
                    <UserProfileModalTrigger userId={userId}>
                        <WorkspaceCollaboratorProfile
                            userId={userId}
                            fullName={fullName}
                            email={email}
                        />
                    </UserProfileModalTrigger>
                </div>
            </td>
            <td className={rowClasses}>
                {!isPending ? (
                    <UsersTableLicenseCell member={member} />
                ) : (
                    <BlurredCell width={100} />
                )}
            </td>
            <td className={rowClasses}>
                {!isPending ? (
                    <UsersTablePluginCell member={member} />
                ) : (
                    <BlurredCell width={120} />
                )}
            </td>
            <td className={classNames(rowClasses, "!pr-0")}>
                <UsersTablePermissionsCell member={member} />
            </td>
        </tr>
    )
}

const BlurredCell = ({ width }: { width: number }) => {
    return <div className="bg-gray-500 blur h-2.5" style={{ width }} />
}

const UsersTableLicenseCell = ({ member }: { member: TableMember }) => {
    const { t } = useTranslation()
    return <div>{t(`${member.license?.subscriptionType || Licenses.Lite}`)}</div>
}

const UsersTablePluginCell = ({ member }: { member: TableMember }) => {
    const hasColorFinder = member.addOns?.colorfinder
    const hasHotspots = member.addOns?.hotspots
    const hasLiveVideo = member.addOns?.liveVideo

    return (
        <div className="flex items-center gap-2 min-w-[100px] h-full">
            {hasColorFinder && (
                <div className="relative group/tooltip">
                    <PluginButton
                        className="!cursor-auto"
                        icon={() => (
                            <Icons.PluginIcon
                                url="/images/plugins/layers/colorfinder.svg"
                                style={{ width: "16px", marginTop: 0, marginRight: 1 }}
                                className="scale-125 cursor-auto"
                            />
                        )}
                    />
                    <Tooltip title="Colorfinder" placement="top" />
                </div>
            )}
            {hasHotspots && (
                <div className="relative group/tooltip">
                    <PluginButton
                        className="!cursor-auto"
                        icon={() => (
                            <Icons.PluginIcon
                                url="/images/plugins/layers/hotspot.svg"
                                className="scale-125 cursor-auto"
                            />
                        )}
                    />
                    <Tooltip title="Hotspots" placement="top" />
                </div>
            )}
            {hasLiveVideo && (
                <div className="relative group/tooltip">
                    <PluginButton
                        className="!cursor-auto"
                        icon={() => (
                            <Icons.PluginIcon
                                url="/images/plugins/layers/live-video.svg"
                                className="scale-125 cursor-auto"
                            />
                        )}
                    />
                    <Tooltip title="Live Video" placement="top" />
                </div>
            )}
        </div>
    )
}

const UsersTablePermissionsCell = ({ member }: { member: TableMember }) => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const { isAdmin, isEditor, selfId, adminCount } = useWorkspacePermissions()
    const isMemberAdmin = member.permissions.workspaceAccess === Permissions.Admin

    const isSelf = member.userId === selfId

    const [isHovering, setIsHovering] = useState(false)

    const openAccesModal = () => {
        dispatch(initializeCenterModalForComponentAction(ModalNames.WorkspaceAccessModal))
        dispatch(setActiveMember(member))
        dispatch(showGlobalModal())
    }

    const enoughRemainingAdmins = adminCount > 1 || !isMemberAdmin
    const canChangeMember = (isAdmin || !isMemberAdmin) && isEditor
    const memberHasRequests = (member.permissions.accessRequestIds?.length || 0) > 0

    const partialEditor = member.permissions?.subRoles?.some(
        (role) => role.permission === Permissions.Editor
    )
    const partialViewer = member.permissions?.subRoles?.some(
        (role) => role.permission === Permissions.Viewer
    )
    let label = member.permissions.workspaceAccess
        ? `Full - ${Permissions[member.permissions.workspaceAccess]}`
        : `Partial - ${partialEditor ? "Editor" : ""}${partialEditor && partialViewer ? "/" : ""}${
              partialViewer ? "Viewer" : ""
          }`
    if (!member.permissions.workspaceAccess && !member.permissions.subRoles)
        label = "Review Request"
    return (
        <div
            className={classNames("flex items-center gap-2 min-w-[185px]", {
                "text-[#D48C49]": memberHasRequests && isEditor,
            })}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            {t(label)}
            {isHovering && !isSelf && canChangeMember && enoughRemainingAdmins && (
                <Icons.SmallIcon
                    Icon={Icons.Edit}
                    className="scale-100 text-gray-100 cursor-pointer hover:text-white-600"
                    onClick={openAccesModal}
                />
            )}
        </div>
    )
}

function ShareButton() {
    const dispatch = useAppDispatch()
    const activeWorkspaceId = useAppSelector(selectActiveWorkspaceId()) || ""

    return (
        <ButtonPrimary
            title="Invite"
            onClick={() => dispatch(openWorkspaceShareModal(activeWorkspaceId))}
        />
    )
}

function ManageSubscriptionsButton() {
    const router = useRouter("/workspace/:workspaceId")
    return (
        <router.Link to={{ path: "/settings/licenses" }}>
            <ButtonSecondary title="Manage Subscriptions" LeftIcon={Icons.Key} />
        </router.Link>
    )
}
